import { CloseOutlined, PlusOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import { Button, Col, Form, Input, InputNumber, Row, Select, Tooltip } from 'antd'
import { IBaseFormProps, PropertyType } from 'Modules'
import { Fragment, useCallback, useEffect, useState } from 'react'

import { PROPERTY_ITEMS } from 'constants/pto'

import AddCustomPropertiesFieldModal from './add-custom-properties-field-modal'

interface IPropertyInformation extends IBaseFormProps {
  propertyTypes: PropertyType[]
  defaultPropertyType?: PropertyType | null
  onChangeLatitude?: (latitude: number) => void
  onChangeLongitude?: (longitude: number) => void
}

const Properties = ({
  form,
  edit = true,
  defaultPropertyType,
  propertyTypes = [],
  onChangeLatitude,
  onChangeLongitude,
}: IPropertyInformation) => {
  const [propertyType, setPropertyType] = useState<PropertyType | null>(null)
  const [, setPreviewPropertyItems] = useState<any>({})
  const [modalOpen, setModalOpen] = useState(false)
  const customMetadataFields = Form.useWatch(['propertyItems', 'customMetadata'], form) || []

  const handlePropertyTypeChange = useCallback(
    (value: string) => {
      const selectedType = propertyTypes.find((type) => type.id === Number(value))
      if (selectedType) {
        setPropertyType(selectedType)
      }
      const currentPropertyItems = form.getFieldValue('propertyItems')
      const prevPropertyItems = currentPropertyItems || {}

      if (selectedType) {
        const propertyItems: any = {}
        selectedType.properties?.map((prop: any) => {
          propertyItems[`${prop}`] =
            currentPropertyItems?.[`${prop}`] || prevPropertyItems[`${prop}`]
        })
        propertyItems.customMetadata = currentPropertyItems?.customMetadata || []
        form.setFieldValue('propertyItems', propertyItems)
      }

      setPreviewPropertyItems(prevPropertyItems)
    },
    [form, propertyTypes]
  )

  useEffect(() => {
    if (defaultPropertyType) {
      handlePropertyTypeChange(defaultPropertyType.id.toString())
    }
  }, [defaultPropertyType, handlePropertyTypeChange])

  const handleAddField = (field: { key: string; value: string; icon: string }) => {
    const customMetadata = form.getFieldValue(['propertyItems', 'customMetadata']) || []
    form.setFieldValue(['propertyItems', 'customMetadata'], [...customMetadata, field])
  }

  const handleRemoveCustomField = (index: number) => {
    const customMetadata = form.getFieldValue(['propertyItems', 'customMetadata']) || []
    customMetadata.splice(index, 1)
    form.setFieldValue(['propertyItems', 'customMetadata'], [...customMetadata])
  }

  return (
    <fieldset>
      <legend>Property Information</legend>
      <Row gutter={24}>
        <Col span={12}>
          <Form.Item
            label={
              <>
                Property Type
                <Tooltip
                  title={
                    <div className="tooltip-box">
                      <p className="tooltip-text">
                        This is your PTO property type. Please select your chosen property. After
                        selecting a property, a map will appear for you to pick the location
                      </p>
                    </div>
                  }
                  placement="right"
                >
                  <QuestionCircleOutlined className="tooltip-icon" />
                </Tooltip>
              </>
            }
            name="type"
            rules={[{ required: true, message: 'Please select the type!' }]}
          >
            <Select
              disabled={!edit}
              placeholder="Select property type"
              onChange={handlePropertyTypeChange}
            >
              {propertyTypes.map((type) => (
                <Select.Option key={type.id} value={type.id}>
                  {type.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label={
              <>
                Latitude
                <Tooltip
                  title={
                    <div className="tooltip-box">
                      <p className="tooltip-text">
                        This is your PTO location coordinates (latitude). You can get the value by
                        entering your chosen latitude as number
                      </p>
                    </div>
                  }
                  placement="right"
                >
                  <QuestionCircleOutlined className="tooltip-icon" />
                </Tooltip>
              </>
            }
            name="latitude"
            rules={[{ required: true, message: 'Please input the latitude!' }]}
          >
            <Input
              disabled={!edit}
              style={{ color: 'rgba(0, 0, 0, 0.85)' }}
              type="number"
              step={'any'}
              min={-180}
              max={180}
              placeholder="Enter latitude"
              value={form.getFieldValue('latitude')}
              onChange={(e) => {
                form.setFieldValue('latitude', parseFloat(e.target.value))
                onChangeLatitude?.(parseFloat(e.target.value))
              }}
            />
          </Form.Item>
        </Col>

        <Col span={12}>
          <Form.Item
            label={
              <>
                Longitude
                <Tooltip
                  title={
                    <div className="tooltip-box">
                      <p className="tooltip-text">
                        This is your PTO location coordinates (longitude). You can get the value by
                        entering your chosen longitude as number
                      </p>
                    </div>
                  }
                  placement="right"
                >
                  <QuestionCircleOutlined className="tooltip-icon" />
                </Tooltip>
              </>
            }
            name="longitude"
            rules={[{ required: true, message: 'Please input the longitude!' }]}
          >
            <Input
              disabled={!edit}
              style={{ color: 'rgba(0, 0, 0, 0.85)' }}
              type="number"
              step={'any'}
              min={-180}
              max={180}
              placeholder="Enter longitude"
              value={form.getFieldValue('longitude')}
              onChange={(e) => {
                form.setFieldValue('longitude', parseFloat(e.target.value))
                onChangeLongitude?.(parseFloat(e.target.value))
              }}
            />
          </Form.Item>
        </Col>

        {propertyType && propertyType.properties?.length
          ? propertyType.properties
              .filter((property) => property !== 'customMetadata')
              .map((property) => {
                const configField = PROPERTY_ITEMS.find((c) => c.field === property)
                const label = configField?.label || property
                const isNumberField =
                  configField?.type === 'number' || configField?.type === 'percent'

                return (
                  <Col span={12} key={property}>
                    <Form.Item
                      name={['propertyItems', property]}
                      label={
                        <Fragment>
                          {label}
                          <Tooltip
                            title={
                              <div className="tooltip-box">
                                <p className="tooltip-text">
                                  This is your PTO {label.toLowerCase()} property,&nbsp;please enter
                                  your chosen&nbsp;
                                  {label.toLowerCase()}
                                  &nbsp;as&nbsp;
                                  {configField?.type || 'text'}
                                </p>
                              </div>
                            }
                            placement="right"
                          >
                            <QuestionCircleOutlined className="tooltip-icon" />
                          </Tooltip>
                        </Fragment>
                      }
                    >
                      {isNumberField ? (
                        <InputNumber
                          style={{
                            color: 'rgba(0, 0, 0, 0.85)',
                            width: '100%',
                          }}
                          min={configField?.min}
                          max={configField?.type === 'percent' ? 100 : undefined}
                          placeholder={`Enter ${label.toLowerCase()}`}
                          disabled={!edit}
                          addonAfter={configField?.addOnAfter || null}
                          addonBefore={configField?.addOnBefore || null}
                        />
                      ) : (
                        <Input
                          style={{ color: 'rgba(0, 0, 0, 0.85)' }}
                          onChange={() => {}}
                          placeholder={`Enter ${label.toLowerCase()}`}
                          disabled={!edit}
                        />
                      )}
                    </Form.Item>
                  </Col>
                )
              })
          : null}

        <Form.Item name={['propertyItems', 'customMetadata']} hidden noStyle />

        {customMetadataFields.map((item: any, index: number) => (
          <Col span={12} key={`custom-${index}`}>
            <Form.Item
              className="custom-property-field"
              label={
                <>
                  {item.key}
                  {edit && (
                    <CloseOutlined
                      className="custom-property-field__delete"
                      onClick={() => handleRemoveCustomField(index)}
                    />
                  )}
                </>
              }
            >
              <Input
                disabled={!edit}
                placeholder={`Enter ${item.key.toLowerCase()}`}
                value={item.value}
                onChange={(e) => {
                  const customMetadata =
                    form.getFieldValue(['propertyItems', 'customMetadata']) || []
                  customMetadata[index] = { ...customMetadata[index], value: e.target.value }
                  form.setFieldValue(['propertyItems', 'customMetadata'], [...customMetadata])
                }}
              />
            </Form.Item>
          </Col>
        ))}
      </Row>

      {edit && (
        <Button type="dashed" onClick={() => setModalOpen(true)} icon={<PlusOutlined />}>
          Add Custom Field
        </Button>
      )}

      <AddCustomPropertiesFieldModal
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onAdd={handleAddField}
      />
    </fieldset>
  )
}

export default Properties
